/**
 * Add Contact Modal used from the share-event flow.
 * Address book edit/create/update modes are handled by ContactView.
 */

import React from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
} from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { closeOutline } from 'ionicons/icons';
import { Contact, ContactLogic } from '@/modules/contact';
import { useContactState } from '@/modules/contact/ContactStateContext';
import { selectAllListingCategories } from '@/modules/contact/reducer';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useContactEditForm } from '@/modules/contact/useContactEditForm';
import ContactEditFormBody from '@/modules/contact/ContactEditFormBody';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
  onSave: (contact: Contact) => void;
}

const AddContactModal: React.FC<AddContactModalProps> = ({
  isOpen,
  onClose: onCancel,
  contact,
  onSave,
}) => {
  const { state } = useContactState();
  const allListingCategories = selectAllListingCategories(state);
  const [presentToast] = useAppToast();
  const { isIOS } = useDeviceInfo();
  const form = useContactEditForm(contact, isOpen);

  const handleDone = () => {
    form.submit(fields => {
      const result = ContactLogic.createReachable(fields);
      if (!result) {
        presentToast({
          message: 'At least one valid U.S. phone number is required',
          duration: 2000,
          color: 'danger',
        });
        return;
      }
      onSave(result.contact);
      presentToast({
        message: 'Contact added successfully',
        duration: 2000,
        color: 'success',
      });
      onCancel();
    });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onCancel}>
      <IonHeader>
        <IonToolbar>
          {isIOS ? (
            <>
              <IonButtons slot="start">
                <IonButton
                  fill="clear"
                  color="medium"
                  onClick={onCancel}
                  aria-label="Cancel"
                  data-testid="add-contact-cancel"
                >
                  Cancel
                </IonButton>
              </IonButtons>
              <IonTitle className="ion-hide-accessible" slot="start">
                Add contact
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  color="success"
                  onClick={handleDone}
                  aria-label="Done"
                  disabled={!form.hasValidPhone}
                >
                  Add
                </IonButton>
              </IonButtons>
            </>
          ) : (
            <>
              <IonButtons slot="start">
                <IonButton
                  fill="clear"
                  color="medium"
                  onClick={onCancel}
                  aria-label="Cancel"
                  data-testid="add-contact-cancel"
                >
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle slot="start">Add contact</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={handleDone}
                  aria-label="Done"
                  color="success"
                  disabled={!form.hasValidPhone}
                >
                  Add
                </IonButton>
              </IonButtons>
            </>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent
        fullscreen
        style={{ '--background': 'var(--app-page-background)' }}
      >
        <ContactEditFormBody
          {...form}
          isAddMode
          allListingCategories={allListingCategories}
        />
      </IonContent>
    </IonModal>
  );
};

export default AddContactModal;
